import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';
import { axisPadLeft, formatPeriodLabel, labelEvery } from './axisPadding';
import { useChartWidth } from './useChartWidth';

export interface StackedColumnDatum { label: string; segments: number[]; }
export interface StackedColumnsProps {
    data: StackedColumnDatum[];
    seriesLabels: string[];
    height?: number;
    colorIndices?: number[];
    tickEvery?: number;
    yFmt?: (n: number) => string;
    /** Compact formatter for axis ticks only (tooltips/table keep the exact yFmt). */
    tickFmt?: (n: number) => string;
}

// Stacked vertical bars — the multi-series sibling of Columns. Each datum's `segments`
// stack bottom-to-top; segment i is painted with theme series color colorIndices[i]
// (default identity). `tickEvery` thins a dense day axis (e.g. 60-day composition).
function niceMax(max: number): number {
    if (max <= 0) return 1;
    const mults = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    for (const m of mults) if (pow * m >= max) return pow * m;
    return pow * 10;
}

export default function StackedColumns({ data, seriesLabels, height = 220, colorIndices, tickEvery = 1, yFmt, tickFmt }: StackedColumnsProps) {
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);

    const [wrapRef, W] = useChartWidth();
    const H = height, padB = 26, padT = 10;
    const colors = colorIndices ?? data[0]?.segments.map((_, i) => i) ?? [];
    const totals = data.map(d => d.segments.reduce((a, b) => a + b, 0));
    const top = niceMax(Math.max(1, ...totals));
    const fmt = yFmt ?? ((n: number) => String(n));
    const tfmt = tickFmt ?? fmt;
    const padL = axisPadLeft([0, 0.25, 0.5, 0.75, 1].map(g => tfmt(top * g)), 10, yFmt ? 36 : 28);
    const plotH = H - padB - padT, plotW = W - padL - 8;
    const bw = Math.min(38, (plotW / Math.max(1, data.length)) * 0.62);
    const step = plotW / Math.max(1, data.length);

    const tickLabels = data.map(d => formatPeriodLabel(d.label, step < 60));
    const every = Math.max(tickEvery, labelEvery(tickLabels, step, 11));

    return (
        <Box ref={wrapRef}>
            <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }} role="img" aria-label="stacked bar chart">
                {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
                    const y = padT + plotH * (1 - g);
                    return (
                        <g key={`grid-${i}`}>
                            <line x1={padL} y1={y} x2={W - 8} y2={y} stroke={c.grid} strokeWidth={1} />
                            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={11} fill={c.muted}>{tfmt(top * g)}</text>
                        </g>
                    );
                })}
                {data.map((d, i) => {
                    const x = padL + step * i + (step - bw) / 2;
                    const tipText = `${formatPeriodLabel(d.label)} — ${d.segments.map((v, s) => `${seriesLabels[s]}: ${fmt(v)}`).join(', ')}`;
                    let acc = 0;
                    return (
                        <g key={`col-${i}`}
                           onMouseMove={e => show(e.clientX, e.clientY, tipText)}
                           onMouseLeave={hide}>
                            {d.segments.map((v, s) => {
                                const h = plotH * (v / top);
                                acc += v;
                                const y = padT + plotH - plotH * (acc / top);
                                return <rect key={s} x={x} y={y} width={bw} height={Math.max(0, h)} fill={c.series[colors[s] ?? s]} />;
                            })}
                            {i % every === 0 &&
                                <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize={11} fill={c.muted}>{tickLabels[i]}</text>}
                        </g>
                    );
                })}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>Table view</Button>
            {showTable && <TableView columns={['Label', ...seriesLabels]} rows={data.map(d => [formatPeriodLabel(d.label), ...d.segments.map(v => fmt(v))])} />}
            {node}
        </Box>
    );
}
